import { AfterViewInit, ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { CONSTANTS } from '@core/constants';
import { DropDownList } from '@core/interfaces';
import { ConfirmationService, EventService, GridActions, GridService, ListService } from '@core/services';
import { remToPx } from '@util/layout';

import { ListOfValues } from '#types/list';

@Component({
  selector: 'z-load-grid',
  templateUrl: './load.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadGridDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  loading$: Observable<boolean>;
  form: FormGroup;

  saveId: string;
  saveName: string;

  display$ = new BehaviorSubject<boolean>(false);

  savedGridDropdownList$ = new BehaviorSubject<DropDownList>(null);
  list: ListOfValues;

  @Input() gridId: string;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  listHeight$ = new BehaviorSubject<any>({ height: '34rem' });

  container: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  constructor(
    private store: Store,
    public gridService: GridService,
    private listService: ListService,
    private confirmationService: ConfirmationService,
    private translocoService: TranslocoService,
    private ngZone: NgZone,
    private eventService: EventService
  ) {}

  ngOnInit() {
    this.gridService
      .savedGridList$(this.gridId)
      .pipe(
        filter((x) => !!x),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((list) => {
        list.data.unshift({ key: 'default', value: 'default', descr: this.translate('core.components.grid.load_dialog.defaults') });

        this.list = list.data;

        this.savedGridDropdownList$.next(
          this.listService.getDropdownList(list, {
            includeHeader: false,
            header1: this.translate('core.services.list.dropdown.header.code'),
            header2: this.translate('core.services.list.dropdown.header.description'),
          })
        );
      });

    this.loading$ = this.eventService.loading(CONSTANTS.REQUEST_LOAD_VIEW);

    this.form = new FormGroup({
      saveId: new FormControl(),
    });

    this.form
      .get('saveId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.saveId = value;
      });

    this.gridService.loadGridPanel$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === 'open') {
        this.store.dispatch(GridActions.ListRequest({ id: this.gridId }));
        this.display$.next(true);

        this.calculatePanelSize();
      } else {
        this.display$.next(false);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  onVisibleChange(event: any) {
    if (event === false) {
      this.close();
    }
  }

  remove() {
    if (!this.form.get('saveId').value) {
      return;
    }

    this.confirmationService.confirm({
      title: this.translate('core.components.grid.load_dialog.confirm_remove.header'),
      message: this.translate('core.components.grid.load_dialog.confirm_remove.message'),
      icon: 'fad fa-triangle-exclamation',
      acceptLabel: this.translate('core.components.grid.load_dialog.confirm_remove.accept_button'),
      rejectLabel: this.translate('core.components.grid.load_dialog.confirm_remove.reject_button'),
      defaultFocus: 'reject',
      accept: () => {
        this.store.dispatch(GridActions.RemoveRequest({ id: this.gridId, saveId: this.form.get('saveId').value }));
      },
      acceptButtonStyleClass: 'p-button-warning',
      rejectButtonStyleClass: 'p-button-secondary',
    });
  }

  onDoubleClick(_event: any) {
    this.load();
  }

  load() {
    if (!this.form.get('saveId').value) {
      return;
    }

    this.list.find((entry) => {
      if (entry.key === this.saveId) {
        this.saveName = entry.value as string;
      }
    });

    this.store.dispatch(GridActions.LoadRequest({ id: this.gridId, saveId: this.saveId, saveName: this.saveName }));
    this.close();
  }

  close() {
    this.form.setValue({
      saveId: null,
    });

    this.store.dispatch(GridActions.closeLoadGridPanel());
  }

  calculatePanelSize() {
    setTimeout(() => {
      if (!this.container) {
        this.initResizeObserver();
      }

      const header: HTMLDivElement = this.container.querySelector('.p-dialog-header');
      const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
      const listFilter: HTMLDivElement = this.container.querySelector('.p-listbox-filter-container');
      const listHeader: HTMLDivElement = this.container.querySelector('.p-listbox-header');

      let offset = 0;

      if (header) {
        offset += Math.floor(header.offsetHeight);
      }

      if (footer) {
        offset += Math.floor(footer.offsetHeight);
      }

      if (listFilter) {
        offset += Math.floor(listFilter.offsetHeight);
      }

      if (listHeader) {
        offset += Math.floor(listHeader.offsetHeight);
      }

      offset += remToPx('2rem');

      this.listHeight$.next({ height: `${Math.floor(this.container.offsetHeight) - offset}px` });
    }, 0);
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.calculatePanelSize();
      });
    });

    try {
      this.container = document.querySelector(`.grid-load-dialog`);

      if (this.container) {
        this.containerResizeObserver.observe(this.container);
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.container) {
        this.containerResizeObserver.unobserve(this.container);
      }
    } catch {}
  }
}
